import { useEffect, useState } from 'react'
import { api } from './api.ts'
import type { VerificationAt } from './route.ts'

/**
 * The uuid for an address, whichever form the address took.
 *
 * Everything inside a conferência is keyed by the verification's uuid -- claiming
 * it, answering a finding, asking for a reverification. Addressing it by branch
 * and quotation number therefore needs one translation, and this is it, kept in
 * front of the screen rather than inside it: `GuidedReview` and
 * `VerificationDetail` still take an id and are unchanged by any of this.
 *
 * A `numbered` address costs one request before the screen can load. A
 * uuid address costs nothing, and neither does returning to a numbered one
 * already resolved -- React keeps the state while the address is the same.
 */
export type Resolved =
  | { readonly state: 'ready'; readonly id: string }
  | { readonly state: 'loading' }
  | { readonly state: 'missing' }

export function useVerificationId(at: VerificationAt): Resolved {
  const [resolved, setResolved] = useState<Resolved>(() =>
    at.kind === 'identified' ? { state: 'ready', id: at.id } : { state: 'loading' },
  )

  const branch = at.kind === 'numbered' ? at.branch : undefined
  const quotation = at.kind === 'numbered' ? at.quotation : undefined
  const id = at.kind === 'identified' ? at.id : undefined

  useEffect(() => {
    if (id !== undefined) {
      setResolved({ state: 'ready', id })
      return
    }
    if (branch === undefined || quotation === undefined) return

    let abandoned = false
    setResolved({ state: 'loading' })
    api
      .verificationIdByNumber(branch, quotation)
      .then((found) => {
        if (!abandoned) setResolved({ state: 'ready', id: found.id })
      })
      .catch(() => {
        // A quotation nobody has a verification for is missing, not broken:
        // the number may simply be wrong, or belong to another account.
        if (!abandoned) setResolved({ state: 'missing' })
      })
    return () => {
      abandoned = true
    }
  }, [id, branch, quotation])

  return resolved
}
